import { useState } from "react";
import { CalculatorPage } from "../../components/CalculatorPage";
import { ResultExplainer } from "../../components/ResultExplainer";
import { ComparisonCard } from "../../components/ComparisonCard";
import { taxAPI, formatCurrency } from "../../lib/api";
import { buildCalculatorSchema } from "../../hooks/useSEO";
import { ComparisonChart } from "../../components/ComparisonChart";
import { EmailGatePdfButton } from "../../components/EmailGatePdfButton";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

const EstateTaxCalculator = () => {
  const [estate, setEstate] = useState("10000000");
  const [married, setMarried] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const run = async () => {
    setLoading(true);
    try {
      const res = await taxAPI.estate({
        estate_value: Number(estate),
        is_married: married,
      });
      setResult(res);
    } catch (e) {
      toast.error("Calculation failed.");
    } finally {
      setLoading(false);
    }
  };

  const inputPanel = (
    <div className="space-y-5">
      <div>
        <label className="font-body text-sm font-semibold block mb-2">Estate Value (USD)</label>
        <input
          type="number"
          inputMode="decimal"
          value={estate}
          onChange={(e) => setEstate(e.target.value)}
          className="w-full px-4 py-3 font-mono text-lg border border-foreground/40 focus:border-primary focus:border-2 focus:outline-none bg-background"
          data-testid="input-estate"
        />
      </div>
      <div className="flex items-center gap-3 border border-foreground/30 p-4">
        <input
          id="married"
          type="checkbox"
          checked={married}
          onChange={(e) => setMarried(e.target.checked)}
          className="w-5 h-5"
          data-testid="input-married"
        />
        <label htmlFor="married" className="font-body text-sm font-semibold">
          Married (doubles exemption via portability)
        </label>
      </div>
      <button
        onClick={run}
        disabled={loading}
        data-testid="calculate-btn"
        className="w-full bg-primary text-primary-foreground font-mono uppercase tracking-widest text-sm px-6 py-4 mt-3 hover:brightness-110 transition flex items-center justify-center gap-2 disabled:opacity-50"
      >
        {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</> : "Calculate Estate Tax"}
      </button>
    </div>
  );

  const resultPanel = result ? (
    <div className="space-y-6" data-testid="estate-result">
      <div className="border-2 border-foreground bg-foreground text-background p-6">
        <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-background/60 mb-2 block">Bottom Line</span>
        <h3 className="font-heading text-2xl md:text-3xl font-bold leading-tight">
          Your heirs owe an additional <span className="text-primary">{formatCurrency(result.additional_tax_2026)}</span> if passing occurs in 2026.
        </h3>
      </div>
      <ComparisonCard
        testid="estate-comparison"
        rows={[
          { label: "Exemption", value2025: result.year_2025.exemption, value2026: result.year_2026.exemption, format: "currency", invertImpact: true },
          { label: "Taxable Estate", value2025: result.year_2025.taxable_amount, value2026: result.year_2026.taxable_amount, format: "currency" },
          { label: "Estate Tax (40%)", value2025: result.year_2025.estate_tax, value2026: result.year_2026.estate_tax, format: "currency" },
          { label: "Effective Rate", value2025: result.year_2025.effective_rate, value2026: result.year_2026.effective_rate, format: "percent" },
        ]}
      />

      <ComparisonChart
        testid="estate-chart"
        title="Estate tax exposure: 2025 vs 2026"
        rows={[
          { label: "Exemption", v2025: result.year_2025.exemption, v2026: result.year_2026.exemption },
          { label: "Taxable Estate", v2025: result.year_2025.taxable_amount, v2026: result.year_2026.taxable_amount },
          { label: "Estate Tax", v2025: result.year_2025.estate_tax, v2026: result.year_2026.estate_tax },
        ]}
      />

      <EmailGatePdfButton
        testid="estate-pdf"
        filename="taxshift-2026-estate.pdf"
        pdfPayload={{
          title: "2026 Estate Tax Analysis",
          subtitle: "Estate exemption under OBBBA (permanent $15M)",
          inputs: [
            { label: "Estate Value", value: formatCurrency(result.estate_value) },
            { label: "Marital Status", value: result.is_married ? "Married (portability)" : "Single" },
          ],
          headline: `Your heirs owe an additional ${formatCurrency(result.additional_tax_2026)} in 2026.`,
          rows: [
            { label: "Exemption", v2025: formatCurrency(result.year_2025.exemption), v2026: formatCurrency(result.year_2026.exemption) },
            { label: "Taxable Estate", v2025: formatCurrency(result.year_2025.taxable_amount), v2026: formatCurrency(result.year_2026.taxable_amount) },
            { label: "Estate Tax (40%)", v2025: formatCurrency(result.year_2025.estate_tax), v2026: formatCurrency(result.year_2026.estate_tax) },
          ],
        }}
      />
      <ResultExplainer slug="estate-tax-2026" />
    </div>
  ) : (
    <div className="border-2 border-dashed border-foreground/30 p-12 text-center bg-card/50" data-testid="estate-empty">
      <p className="font-heading text-xl">Enter your estate value and click Calculate.</p>
    </div>
  );

  return (
    <CalculatorPage
      title="2026 Estate Tax Exemption Calculator"
      slug="estate-tax-2026"
      overline="Calculator · Estate Planning"
      description="OBBBA permanently raised the federal estate/gift tax exemption to $15M (single) / $30M (married joint) for 2026, up from $13.99M / $27.98M in 2025. Calculate your estate's exposure under current law."
      seoTitle="2026 Estate Tax Exemption Calculator — OBBBA $15M Permanent"
      seoKeywords="estate tax 2026, gift tax exemption 2026, inheritance tax calculator, estate planning 2026"
      jsonLd={buildCalculatorSchema({
        name: "2026 Estate Tax Calculator",
        description: "Free calculator for 2026 federal estate tax under OBBBA's permanent $15M exemption.",
        url: "/calculator/estate-tax-2026",
      })}
      inputPanel={inputPanel}
      resultPanel={resultPanel}
      education={
        <>
          <h2 className="font-heading text-3xl md:text-4xl font-bold tracking-tight mb-6">
            Estate exemption raised to $15M — permanently
          </h2>
          <div className="font-body text-base leading-relaxed space-y-4 text-foreground/85">
            <p>
              OBBBA made the doubled federal estate, gift, and GST exemption permanent AND raised it
              to <strong>$15 million per individual</strong> ($30 million per married couple with
              portability) for 2026, up from $13.99M/$27.98M in 2025. Annual inflation adjustments
              continue indefinitely.
            </p>
            <p>
              This effectively eliminated the "2026 sunset cliff" that would have cut the exemption
              roughly in half. Families who accelerated lifetime gifting in 2024-2025 to capture the
              doubled exemption before sunset no longer face that urgency.
            </p>
            <p>
              The annual gift tax exclusion (separate from the lifetime exemption) is $19,000 per
              recipient in 2026, with no change from 2025. Spousal Lifetime Access Trusts (SLATs),
              GRATs, and dynasty trusts remain valuable planning tools — just at a less accelerated
              pace than during the 2024-2025 pre-sunset urgency.
            </p>
          </div>
        </>
      }
      relatedSlugs={[
        { slug: "capital-gains-2026", label: "Capital Gains" },
        { slug: "tax-bracket-2026", label: "Tax Bracket" },
      ]}
    />
  );
};

export default EstateTaxCalculator;
